import { CdkDragDrop, transferArrayItem } from "@angular/cdk/drag-drop";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { MatMenu } from "@angular/material/menu";
import {
  createUpsPackageDescription,
  getTemplateTypeLabelKey,
} from "../../card-orders/card-order.util";
import { FULFILLMENT_BLOCKED_REASON } from "../../card-orders/card-orders.interface";
import {
  isNfcPrintCardTemplateType,
  printCardOrderItemEncodedCount,
} from "../print-card-nfc.util";
import type {
  PrintCardUpsPackLineItem,
  PrintCardUpsPackageDraft,
  UpsShipmentOptions,
} from "../print-card-orders.interface";

@Component({
  selector: "app-ups-package-planner",
  templateUrl: "./ups-package-planner.component.html",
  styleUrls: ["./ups-package-planner.component.scss"],
  standalone: false,
})
export class UpsPackagePlannerComponent implements OnChanges {
  @Input() lineItems: PrintCardUpsPackLineItem[] = [];
  @Input() packages: PrintCardUpsPackageDraft[] = [];
  @Input() upsOptions: UpsShipmentOptions | null = null;
  @Input() disabled = false;
  @Input() packerName: string | null = null;

  @Output() packagesChange = new EventEmitter<PrintCardUpsPackageDraft[]>();
  @Output() autoArrange = new EventEmitter<void>();
  @Output() previewItem = new EventEmitter<PrintCardUpsPackLineItem>();

  unassignedItems: PrintCardUpsPackLineItem[] = [];
  packageDropListIds: string[] = [];
  readonly unassignedDropListId = "ups-unassigned-orders";

  @ViewChild("moveMenu", { static: true }) moveMenu!: MatMenu;

  moveMenuItem: PrintCardUpsPackLineItem | null = null;
  moveMenuTargets: Array<{ id: string; index: number }> = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["lineItems"] || changes["packages"]) {
      this.packageDropListIds = this.packages.map((pkg) =>
        this.dropListId(pkg.id),
      );
      this.enforceUnassignableItemsUnassigned();
      this.syncUnassignedItems();
    }
  }

  private enforceUnassignableItemsUnassigned(): void {
    const unassignableNumbers = new Set(
      this.lineItems
        .filter((item) => this.isItemUnassignable(item))
        .map((item) => item.printerOrderNumber),
    );
    if (unassignableNumbers.size === 0) {
      return;
    }
    const hasUnassignableAssigned = this.packages.some((pkg) =>
      pkg.printerOrderNumbers.some((number) => unassignableNumbers.has(number)),
    );
    if (!hasUnassignableAssigned) {
      return;
    }
    this.emitPackages(
      this.packages.map((pkg) => ({
        ...pkg,
        printerOrderNumbers: pkg.printerOrderNumbers.filter(
          (number) => !unassignableNumbers.has(number),
        ),
      })),
    );
  }

  isItemFulfillmentBlocked(item: PrintCardUpsPackLineItem): boolean {
    return (
      item.fulfillmentBlockedReason ===
      FULFILLMENT_BLOCKED_REASON.EMPLOYEE_DELETED
    );
  }

  isItemLocked(item: PrintCardUpsPackLineItem): boolean {
    if (!isNfcPrintCardTemplateType(item.templateType)) {
      return false;
    }
    return printCardOrderItemEncodedCount(item) < item.quantity;
  }

  isItemUnassignable(item: PrintCardUpsPackLineItem): boolean {
    return this.isItemFulfillmentBlocked(item) || this.isItemLocked(item);
  }

  isValid(): boolean {
    if (this.packages.length === 0) {
      return false;
    }

    const packagesValid = this.packages.every(
      (pkg) =>
        pkg.packageWeight > 0 &&
        pkg.packageLength > 0 &&
        pkg.packageWidth > 0 &&
        pkg.packageHeight > 0,
    );
    if (!packagesValid) {
      return false;
    }

    if (this.lineItems.length === 0) {
      return true;
    }

    const assigned = new Set(
      this.packages.flatMap((pkg) => pkg.printerOrderNumbers),
    );
    const assignableItems = this.lineItems.filter(
      (item) => !this.isItemUnassignable(item),
    );
    return (
      assignableItems.every((item) => assigned.has(item.printerOrderNumber)) &&
      this.packages.every((pkg) => pkg.printerOrderNumbers.length > 0)
    );
  }

  get showAllAssignedMessage(): boolean {
    return this.unassignedItems.length === 0;
  }

  get showUnassignedPool(): boolean {
    if (!this.disabled) {
      return this.lineItems.length > 0;
    }
    return this.lineItems.some((item) => this.isItemUnassignable(item));
  }

  dropListId(packageId: string): string {
    return `ups-package-${packageId}`;
  }

  connectedDropLists(packageId: string): string[] {
    return [
      this.unassignedDropListId,
      ...this.packageDropListIds.filter(
        (id) => id !== this.dropListId(packageId),
      ),
    ];
  }

  itemsForPackage(pkg: PrintCardUpsPackageDraft): PrintCardUpsPackLineItem[] {
    return this.lineItems.filter((item) =>
      pkg.printerOrderNumbers.includes(item.printerOrderNumber),
    );
  }

  addPackage(): void {
    const defaults = this.upsOptions?.defaults;
    const nextIndex = this.packages.length + 1;
    this.emitPackages([
      ...this.packages,
      {
        id: `pkg-${Date.now()}-${nextIndex}`,
        printerOrderNumbers: [],
        packageWeight: defaults?.packageWeight ?? 0.5,
        packageLength: 30,
        packageWidth: 22,
        packageHeight: 10,
        weightUnit: defaults?.weightUnit ?? "KGS",
        dimensionUnit: defaults?.dimensionUnit ?? "CM",
        packagingCode: defaults?.packagingCode ?? "02",
        packageDescription: createUpsPackageDescription(this.packerName),
      },
    ]);
  }

  removePackage(packageId: string): void {
    this.emitPackages(this.packages.filter((pkg) => pkg.id !== packageId));
  }

  get canResetPackaging(): boolean {
    return this.packages.some((pkg) => pkg.printerOrderNumbers.length > 0);
  }

  resetPackaging(): void {
    this.emitPackages(
      this.packages.map((pkg) => ({
        ...pkg,
        printerOrderNumbers: [],
      })),
    );
  }

  onDrop(
    event: CdkDragDrop<PrintCardUpsPackLineItem[]>,
    targetPackageId?: string,
  ): void {
    if (this.disabled) {
      return;
    }

    const item = event.item.data as PrintCardUpsPackLineItem;
    if (!item?.printerOrderNumber) {
      return;
    }

    if (event.previousContainer !== event.container) {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }

    this.moveItem(item, targetPackageId);
  }

  unassignItem(item: PrintCardUpsPackLineItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (this.disabled || !item?.printerOrderNumber) {
      return;
    }
    this.moveItem(item);
  }

  openMoveMenu(item: PrintCardUpsPackLineItem, event: Event): void {
    event.stopPropagation();
    this.moveMenuItem = item;
    this.moveMenuTargets = this.packageTargetsForItem(item);
  }

  selectMoveTarget(targetPackageId: string): void {
    const item = this.moveMenuItem;
    this.moveMenuItem = null;
    this.moveMenuTargets = [];
    if (!item?.printerOrderNumber || !targetPackageId || this.disabled) {
      return;
    }
    this.moveItem(item, targetPackageId);
  }

  packageTargetsForItem(
    item: PrintCardUpsPackLineItem,
  ): Array<{ id: string; index: number }> {
    const currentPackageId = this.packages.find((pkg) =>
      pkg.printerOrderNumbers.includes(item.printerOrderNumber),
    )?.id;
    return this.packages
      .map((pkg, index) => ({ id: pkg.id, index: index + 1 }))
      .filter((pkg) => pkg.id !== currentPackageId);
  }

  private moveItem(
    item: PrintCardUpsPackLineItem,
    targetPackageId?: string,
  ): void {
    if (targetPackageId && this.isItemUnassignable(item)) {
      return;
    }

    const nextPackages = this.packages.map((pkg) => ({
      ...pkg,
      printerOrderNumbers: pkg.printerOrderNumbers.filter(
        (number) => number !== item.printerOrderNumber,
      ),
    }));

    if (targetPackageId) {
      const target = nextPackages.find((pkg) => pkg.id === targetPackageId);
      if (
        target &&
        !target.printerOrderNumbers.includes(item.printerOrderNumber)
      ) {
        target.printerOrderNumbers = [
          ...target.printerOrderNumbers,
          item.printerOrderNumber,
        ];
        if (!target.packageDescription?.trim()) {
          target.packageDescription = createUpsPackageDescription(
            this.packerName,
          );
        }
        target.packageWeight = target.printerOrderNumbers.reduce(
          (sum, number) => {
            const line = this.lineItems.find(
              (entry) => entry.printerOrderNumber === number,
            );
            return sum + (line?.estimatedWeightKg ?? 0.1);
          },
          0.05,
        );
        target.packageWeight = Math.max(
          0.1,
          Math.round(target.packageWeight * 100) / 100,
        );
      }
    }

    this.emitPackages(nextPackages);
  }

  trackPackageById(_index: number, pkg: PrintCardUpsPackageDraft): string {
    return pkg.id;
  }

  updatePackageField(
    packageId: string,
    field: keyof PrintCardUpsPackageDraft,
    rawValue: string | number,
  ): void {
    const target = this.packages.find((pkg) => pkg.id === packageId);
    if (!target || target[field] === rawValue) {
      return;
    }

    queueMicrotask(() => {
      Object.assign(target, { [field]: rawValue });
      this.packagesChange.emit(this.packages);
    });
  }

  templateTypeLabelKey(templateType: string): string {
    return getTemplateTypeLabelKey(templateType);
  }

  onPreviewItem(item: PrintCardUpsPackLineItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.previewItem.emit(item);
  }

  canPreviewItem(item: PrintCardUpsPackLineItem): boolean {
    return Boolean(item.templateId?.trim() && item.employeeEmail?.trim());
  }

  private emitPackages(packages: PrintCardUpsPackageDraft[]): void {
    this.packages = packages;
    this.packageDropListIds = packages.map((pkg) => this.dropListId(pkg.id));
    this.packagesChange.emit(packages);
    this.syncUnassignedItems();
  }

  private syncUnassignedItems(): void {
    const assigned = new Set(
      this.packages.flatMap((pkg) => pkg.printerOrderNumbers),
    );
    this.unassignedItems = this.lineItems.filter(
      (item) =>
        this.isItemFulfillmentBlocked(item) ||
        !assigned.has(item.printerOrderNumber),
    );
  }
}
